import { type CSSProperties, useEffect, useRef, useState } from 'react';
import styles from './StatusIndicator.module.css';

const TYPEWRITER_BASE_CHARS_PER_SEC = 40;
// Each instance jitters its pace within ±30% of the base rate so concurrent
// rows (e.g. sibling sub-task chips that all start together) don't reveal in
// obviously synchronized lockstep.
const TYPEWRITER_JITTER = 0.3;

// Reveals text progressively rather than swapping the whole string in at
// once, so status/sub-task messages read as "typed out" instead of flashing
// in — callers key={text} this so a new message remounts and restarts the
// reveal (matching the existing labelFadeIn/textSweep key-remount pattern).
export function TypewriterText({ text, className }: { text: string; className?: string }) {
  const [revealed, setRevealed] = useState(0);
  const rafRef = useRef<number | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: text excluded — callers remount via key={text} to restart the reveal, rather than this effect re-running mid-reveal
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setRevealed(text.length);
      return;
    }
    const jitter = 1 + (Math.random() * 2 - 1) * TYPEWRITER_JITTER;
    const charsPerSec = TYPEWRITER_BASE_CHARS_PER_SEC * jitter;
    const start = performance.now();
    function tick(now: number) {
      const elapsed = (now - start) / 1000;
      const count = Math.min(text.length, Math.floor(elapsed * charsPerSec));
      setRevealed(count);
      if (count < text.length) {
        rafRef.current = requestAnimationFrame(tick);
      }
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return <span className={className}>{text.slice(0, revealed)}</span>;
}

export type StatusVariant = 'ripple' | 'wave' | 'tide' | 'drop' | 'sonar' | 'dhiwave' | 'capillary';

interface Props {
  message?: string;
  variant?: StatusVariant;
}

// ---------------------------------------------------------------------------
// DHI Wave — a single wave line driven by requestAnimationFrame.
// The wave is a superposition of three sine components whose angular
// frequencies follow the deep-water dispersion relation (ω ∝ √k), so
// they interact the way real water surface waves do. Catmull-Rom → cubic
// bezier interpolation makes the sampled path perfectly smooth.
// ---------------------------------------------------------------------------
function DHIWave() {
  const pathRef = useRef<SVGPathElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const W = 64; // viewBox width
    const CY = 9; // vertical centre in viewBox "0 0 64 18"
    const N = 14; // number of sample points along x

    // Wave components: [amplitude, wave-number k, angular-speed ω, initial phase]
    // ω values loosely satisfy ω ∝ √k (deep-water dispersion).
    const WAVES: [number, number, number, number][] = [
      [4.0, 0.12, 0.9, 0.0], // long swell      — slow, large
      [2.0, 0.23, 1.72, 0.9], // wind chop       — medium
      [0.8, 0.41, 2.58, 2.3], // surface ripples — fast, small
    ];

    function sample(t: number): [number, number][] {
      const pts: [number, number][] = [];
      for (let i = 0; i <= N; i++) {
        const x = (i / N) * W;
        let y = CY;
        for (const [A, k, w, phi] of WAVES) {
          y += A * Math.sin(k * x - w * t + phi);
        }
        pts.push([x, y]);
      }
      return pts;
    }

    // Catmull-Rom → cubic bezier so the path is smooth through every point.
    function toPath(pts: [number, number][]): string {
      let d = `M ${pts[0][0].toFixed(2)},${pts[0][1].toFixed(2)}`;
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[Math.max(0, i - 1)];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[Math.min(pts.length - 1, i + 2)];
        const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
        const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
        const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
        const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
        d += ` C ${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`;
      }
      return d;
    }

    let t = 0;
    function tick() {
      t += 0.022; // time step — controls overall animation speed
      pathRef.current?.setAttribute('d', toPath(sample(t)));
      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div className={styles.dhiWaveContainer}>
      <svg
        aria-hidden="true"
        viewBox="0 0 64 18"
        width="64"
        height="18"
        className={styles.dhiWaveSvg}
      >
        <path
          ref={pathRef}
          stroke="var(--accent)"
          strokeWidth="1.8"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Capillary Ripple — three drop points scattered across the box, each emitting
// a burst of three expanding rings (thick + opaque at the centre, thinning and
// fading outward), staggered a third of a cycle apart. Each group jumps to a
// fresh random position during its quiet phase (rings faded out), so the drops
// land somewhere new every cycle, like rain on still water.
// ---------------------------------------------------------------------------

// One shared cycle clock: the CSS animation durations (via the --cycle custom
// property) and the reposition timer both derive from it, so retuning the
// speed can't put the position jump back inside the visible phase.
const CAPILLARY_CYCLE_MS = 3600;
// Three groups offset by a third of a cycle each.
const CAPILLARY_DELAYS_MS = [0, CAPILLARY_CYCLE_MS / 3, (CAPILLARY_CYCLE_MS * 2) / 3];
// Rings and dot are fully faded from 50% of the cycle on (see the keyframes);
// reposition at 75%, safely inside that quiet phase.
const CAPILLARY_REPOSITION_MS = CAPILLARY_CYCLE_MS * 0.75;

// Random drop position: inset vertically (rings expand ~35 units past the
// drop) so ripples stay out of the content above and below the box, and kept
// at a minimum distance from the other groups' drops so bursts don't stack on
// top of each other. Rejection-sampled; gives up after a few tries since an
// occasional close pair is harmless.
const MIN_DROP_DIST = 28;
function pickSpot(taken: [number, number][], self: number): [number, number] {
  let x = 50;
  let y = 50;
  for (let tries = 0; tries < 12; tries++) {
    x = 20 + Math.random() * 60;
    y = 30 + Math.random() * 40;
    const clear = taken.every(
      ([tx, ty], i) => i === self || Math.hypot(x - tx, y - ty) >= MIN_DROP_DIST,
    );
    if (clear) break;
  }
  return [x, y];
}

function CapillaryRipple() {
  const groupRefs = useRef<(SVGGElement | null)[]>([]);

  useEffect(() => {
    // Reduced motion: CSS disables the animations; leave every circle at the
    // static centre and skip the reposition machinery entirely.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const timers: number[] = [];
    const cleanups: (() => void)[] = [];
    // Current drop position per group, shared so each new spot keeps its
    // distance from the others.
    const positions: [number, number][] = [];

    groupRefs.current.forEach((g, gi) => {
      if (!g) return;
      const place = () => {
        const [x, y] = pickSpot(positions, gi);
        positions[gi] = [x, y];
        for (const c of g.querySelectorAll('circle')) {
          c.setAttribute('cx', x.toFixed(1));
          c.setAttribute('cy', y.toFixed(1));
        }
      };
      place();
      // The group's own no-op animation marks the cycle boundary; reposition
      // partway into the next cycle, inside the quiet phase (opacity 0), so
      // the jump is never visible. Ring/dot animations bubble the same event
      // up, hence the target check.
      const onIteration = (e: AnimationEvent) => {
        if (e.target !== g) return;
        timers.push(window.setTimeout(place, CAPILLARY_REPOSITION_MS));
      };
      g.addEventListener('animationiteration', onIteration);
      cleanups.push(() => g.removeEventListener('animationiteration', onIteration));
    });

    return () => {
      for (const fn of cleanups) fn();
      for (const t of timers) clearTimeout(t);
    };
  }, []);

  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 100 100"
      width="72"
      height="72"
      className={styles.capillaryContainer}
      style={{ '--cycle': `${CAPILLARY_CYCLE_MS}ms` } as CSSProperties}
    >
      {CAPILLARY_DELAYS_MS.map((delay, i) => (
        <g
          key={delay}
          ref={(el) => {
            groupRefs.current[i] = el;
          }}
          className={styles.capillaryGroup}
          style={{ '--delay': `${delay}ms` } as CSSProperties}
        >
          <circle className={styles.capRingA} cx="50" cy="50" r="0" fill="none" />
          <circle className={styles.capRingB} cx="50" cy="50" r="0" fill="none" />
          <circle className={styles.capRingC} cx="50" cy="50" r="0" fill="none" />
          <circle className={styles.capDot} cx="50" cy="50" r="2.5" />
        </g>
      ))}
    </svg>
  );
}

// Concentric expanding rings bursting from a central drop. Exported standalone
// (viewBox stays fixed, only width/height scale) so callers needing a smaller
// footprint — e.g. one per row in a list — aren't stuck with the 72px default.
export function RippleIcon({ size = 72 }: { size?: number }) {
  // .rippleContainer's margin-right compensates for the ring animation's empty
  // padding at the 72px default; scale it with size so smaller instances (e.g.
  // one per row in a list) don't inherit an oversized negative margin.
  const compensatingMargin = (size * (-14 / 72)).toFixed(1);
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className={styles.rippleContainer}
      style={{ marginRight: `${compensatingMargin}px` }}
    >
      <circle
        cx="100"
        cy="100"
        r="0"
        fill="none"
        stroke="var(--accent)"
        className={styles.rippleR1a}
      />
      <circle
        cx="100"
        cy="100"
        r="0"
        fill="none"
        stroke="var(--accent)"
        className={styles.rippleR1b}
      />
      <circle
        cx="100"
        cy="100"
        r="0"
        fill="none"
        stroke="var(--accent)"
        className={styles.rippleR1c}
      />
      <circle
        cx="100"
        cy="100"
        r="0"
        fill="none"
        stroke="var(--accent)"
        className={styles.rippleR2a}
      />
      <circle
        cx="100"
        cy="100"
        r="0"
        fill="none"
        stroke="var(--accent)"
        className={styles.rippleR2b}
      />
      <circle
        cx="100"
        cy="100"
        r="0"
        fill="none"
        stroke="var(--accent)"
        className={styles.rippleR2c}
      />
      <circle cx="100" cy="100" r="5" fill="var(--accent)" className={styles.rippleDrop} />
    </svg>
  );
}

// ---------------------------------------------------------------------------

export default function StatusIndicator({
  message = 'Searching documentation...',
  variant = 'ripple',
}: Props) {
  return (
    <div className={styles.indicator}>
      {variant === 'ripple' && <RippleIcon />}

      {variant === 'wave' && (
        <div className={styles.waveContainer}>
          <div className={styles.bar} />
          <div className={styles.bar} />
          <div className={styles.bar} />
          <div className={styles.bar} />
          <div className={styles.bar} />
        </div>
      )}

      {variant === 'tide' && (
        <div className={styles.tideContainer}>
          <div className={styles.tideFill} />
        </div>
      )}

      {variant === 'drop' && (
        <div className={styles.dropContainer}>
          <div className={styles.drop} />
        </div>
      )}

      {variant === 'sonar' && (
        <div className={styles.sonarContainer}>
          <div className={styles.sonarCore} />
          <div className={styles.sonarRing} />
        </div>
      )}

      {variant === 'dhiwave' && <DHIWave />}

      {variant === 'capillary' && <CapillaryRipple />}

      <TypewriterText key={message} text={message} className={styles.label} />
    </div>
  );
}
